import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Upload, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/pages/Portal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const CATS = ["extinguishers", "alarms", "suppression"];
const EMPTY = { name: "", category: "extinguishers", description: "", price: "", stock: "", sku: "", image_url: "" };

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...p, price: String(p.price), stock: String(p.stock) }); setEditId(p.id); setOpen(true); };

  const uploadImage = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/product", fd);
      setForm((f) => ({ ...f, image_url: `${API}/public/files/${data.path}` }));
      toast.success("Image uploaded");
    } catch (err) { toast.error("Image upload failed"); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name) return toast.error("Product name is required");
    const payload = { ...form, price: parseFloat(form.price) || 0, stock: parseInt(form.stock) || 0 };
    try {
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Product updated" : "Product added");
      setOpen(false); load();
    } catch (e) { toast.error("Could not save product"); }
  };

  const del = async (id) => { if (!window.confirm("Delete this product? This cannot be undone.")) return; await api.delete(`/products/${id}`); toast.success("Product deleted"); load(); };

  return (
    <div className="p-8">
      <PageHeader title="Products" subtitle="Inventory">
        <Button onClick={openNew} className="rounded-sm bg-primary hover:bg-primary/90 gap-2" data-testid="new-product-btn"><Plus size={16} /> Add Product</Button>
      </PageHeader>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <Table className="min-w-[720px]">
          <TableHeader><TableRow className="bg-muted/50">
            <TableHead className="w-16">Image</TableHead><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>SKU</TableHead><TableHead>Price</TableHead><TableHead>Stock</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                <TableCell>
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-10 h-10 object-cover rounded-sm border border-border" data-testid={`product-thumb-${p.id}`} />
                  ) : (
                    <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center text-muted-foreground"><ImageIcon size={16} /></div>
                  )}
                </TableCell>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="capitalize">{p.category}</TableCell>
                <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                <TableCell>₹{p.price.toLocaleString("en-IN")}</TableCell>
                <TableCell><Badge className={`rounded-sm ${p.stock < 10 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>{p.stock}</Badge></TableCell>
                <TableCell className="text-right space-x-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil size={15} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(p.id)} className="text-primary" data-testid={`delete-product-${p.id}`}><Trash2 size={15} /></Button>
                </TableCell>
              </TableRow>
            ))}
            {products.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No products yet. Add your first product.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs uppercase">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" data-testid="prod-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="rounded-sm mt-1" data-testid="prod-category"><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label className="text-xs uppercase">SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="rounded-sm mt-1" data-testid="prod-sku" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase">Ref. Price (₹, internal)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-sm mt-1" data-testid="prod-price" /></div>
              <div><Label className="text-xs uppercase">Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-sm mt-1" data-testid="prod-stock" /></div>
            </div>
            <div>
              <Label className="text-xs uppercase flex items-center gap-1"><ImageIcon size={12} /> Product Image</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.image_url ? (
                  <img src={form.image_url} alt="preview" className="w-16 h-16 object-cover rounded-sm border border-border" data-testid="prod-image-preview" />
                ) : (
                  <div className="w-16 h-16 rounded-sm bg-muted flex items-center justify-center text-muted-foreground shrink-0"><ImageIcon size={20} /></div>
                )}
                <div className="flex-1">
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-border text-sm cursor-pointer hover:bg-muted transition-colors" data-testid="prod-image-upload-label">
                    <Upload size={14} /> {uploading ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/*" className="hidden" onChange={uploadImage} disabled={uploading} data-testid="prod-image-upload" />
                  </label>
                  <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="…or paste an image URL" className="rounded-sm mt-2 text-xs" data-testid="prod-image-url" />
                </div>
              </div>
            </div>
            <div><Label className="text-xs uppercase">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="save-product-btn">{editId ? "Update" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
